import { getKnowledgeDocuments, uploadDocument, validateKnowledgeDocuments } from '@/services/api';
import { logger } from '@/services/logger';
import { getFileType, renameFile } from '@/utils/utils';
import { DocumentType, IKnowledgeDocument } from '@d-id/types';
import { nanoid } from 'nanoid';
import { AgentFileS3UploadStatus, UploadingFile } from './types';

export const handleFileValidation = async (files: UploadingFile[], mixpanel: any, agentId?: string) => {
    try {
        const uploaded = files.filter(item => item.status === AgentFileS3UploadStatus.Uploaded);
        const documents: IKnowledgeDocument[] = uploaded.map(
            ({ file, s3Url }) =>
                ({
                    title: file.name,
                    source_url: s3Url,
                    documentType: file.type,
                }) as IKnowledgeDocument
        );
        const validationResults = await validateKnowledgeDocuments(documents);
        const mapFileValidation = Object.fromEntries(validationResults.map((file: { title: any }) => [file.title, file])) as Record<
            string,
            any
        >;
        const updatedFiles = files.map(file => ({
            ...file,
            status: mapFileValidation[file.file.name].error ? AgentFileS3UploadStatus.Error : AgentFileS3UploadStatus.Validated,
            error: mapFileValidation[file.file.name].error,
        }));
        mixpanel?.track('agent-document-validation', { event: 'success', agentId });
        return updatedFiles;
    } catch (error) {
        logger.error('File validation failed', { files, error });
        const updatedFiles = files.map(file => ({
            ...file,
            status: AgentFileS3UploadStatus.Error,
            error: 'Failed to validate file',
        }));
        mixpanel?.track('agent-document-validation', { event: 'error', agentId });
        return updatedFiles;
    }
};

export const handleFileUploadAndValidation = async (files: File[], setUploadingFiles: any, mixpanel: any, agentId?: string) => {
    const uploadPromises = files.map(async (file: File) => {
        const uploadingFile = {
            file: new File([file], file.name, {
                type: getFileType(file.type),
                lastModified: file.lastModified,
            }),
            status: AgentFileS3UploadStatus.Uploading,
        };

        setUploadingFiles((prevFiles: UploadingFile[]) => [...prevFiles, uploadingFile]);

        try {
            const s3Url =
                uploadingFile.file.type === DocumentType.Html ? file.name : await uploadDocument(renameFile(file, `docmt_${nanoid()}`));
            const updatedFile = { ...uploadingFile, status: AgentFileS3UploadStatus.Uploaded, s3Url };
            setUploadingFiles((prevFiles: UploadingFile[]) => prevFiles.map(item => (item.file === file ? updatedFile : item)));
            mixpanel?.track('agent-document-uploaded', { event: 'success', agentId });
            return updatedFile;
        } catch (error) {
            logger.error('Uploading for file failed', { file, error });
            const updatedFile = { ...uploadingFile, status: AgentFileS3UploadStatus.Error, s3Url: null, error: 'Failed to upload file' };
            setUploadingFiles((prevFiles: UploadingFile[]) => prevFiles.map(item => (item.file === file ? updatedFile : item)));
            mixpanel?.track('agent-document-uploaded', { event: 'error', agentId });
            return updatedFile;
        }
    });

    const uploadedFiles = (await Promise.all(uploadPromises)) as UploadingFile[];
    const updatedFiles = await handleFileValidation(uploadedFiles, mixpanel, agentId);
    setUploadingFiles((prevFiles: UploadingFile[]) => {
        return prevFiles.map(prevFile => updatedFiles.find(newFile => newFile.file === prevFile.file) || prevFile);
    });
};

const mapKnowledgeDocuments = (documents: IKnowledgeDocument[]): any => {
    if (!documents?.length) {
        return [];
    }

    return documents.map((document: IKnowledgeDocument) => ({
        status: AgentFileS3UploadStatus.Validated,
        s3Url: document.source_url,
        id: document.id,
        file: {
            lastModified: new Date(document.created_at!).getTime(),
            path: document.title,
            name: document.title,
            type: document.documentType,
        },
    }));
};

export const getKnowledgeMappedDocuments = async (knowledge_id: string) => {
    const documents = await getKnowledgeDocuments(knowledge_id);
    const mappedDocuments = mapKnowledgeDocuments(documents);

    return mappedDocuments;
};
